import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ApiBaseUrlContext } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './OperatorPage.css';
import '../topmenu.css';
import '../forms.css';

const OperatorPage = () => {
  const { scheduleid } = useParams();
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const { t } = useTranslation();
  const [schedule, setSchedule] = useState(null);
  const [machineName, setMachineName] = useState('');
  const [zoomImage, setZoomImage] = useState(null);
  const navigate = useNavigate();
  const [headUser, setHeadUser] =useState('');
  // NEW: store the “email to send to”
  //const [recipientEmail, setRecipientEmail] = useState('');

  // Create a ref to the part of the page we’ll convert to PDF
  const pdfContentRef = useRef(null);

  useEffect(() => {
    const fetchScheduleData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error(t('OperatorPage.NoTokenFound'));
        navigate('/login');
        return;
      }
      try {
        const scheduleResponse = await axios.get(
          `${apiBaseUrl}/mnt/api/maintenance/schedule/${scheduleid}`,
          {
            headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
          }
        );

        const machineResponse = await axios.get(
          `${apiBaseUrl}/mnt/api/machines/${scheduleResponse.data.machineid}`,
          {
            headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
          }
        );

        setSchedule(scheduleResponse.data);
        setMachineName(machineResponse.data.machine.toUpperCase());
        setHeadUser(machineResponse.data.head_user);
      } catch (error) {
        console.error(t('OperatorPage.ErrorFetchingData'), error);
      }
    };

    fetchScheduleData();
  }, [apiBaseUrl, scheduleid, navigate, t]);

  const isAllChecked = () => {
    return schedule?.checklist.every(
      (item) =>
        item.status === 'yes' ||
        item.status === 'header' ||
        item.status === 'text' ||
        item.status === 'file'
    );
  };

  const handleChecklistChange = async (index) => {
    const updatedChecklist = [...schedule.checklist];
    const newStatus = updatedChecklist[index].status === 'yes' ? 'no' : 'yes';
    updatedChecklist[index].status = newStatus;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${apiBaseUrl}/mnt/api/maintenance/checklist/${updatedChecklist[index].checklistid}`,
        {
          status: newStatus,
          description: updatedChecklist[index].description,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSchedule({ ...schedule, checklist: updatedChecklist });
    } catch (error) {
      console.error(t('OperatorPage.ErrorUpdatingChecklist'), error);
    }
  };

  const handleTextChange = async (index, newDescription) => {
    const updatedChecklist = [...schedule.checklist];
    updatedChecklist[index].description = newDescription;
    setSchedule({ ...schedule, checklist: updatedChecklist });

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${apiBaseUrl}/mnt/api/maintenance/checklist/${updatedChecklist[index].checklistid}`,
        { description: newDescription, status: 'text' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error(t('OperatorPage.ErrorUpdatingChecklist'), error);
    }
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem('token');
    try {
      if (!isAllChecked()) {
        alert(t('OperatorPage.SavedButNotSubmitted'));
        navigate('/maintenance-schedule');
        return;
      }

      await axios.patch(
        `${apiBaseUrl}/mnt/api/maintenance/${schedule.scheduleid}/status`,
        { status: 'Closed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(t('OperatorPage.ChecklistSubmitted'));
      navigate('/maintenance-schedule');
    } catch (error) {
      console.error(t('OperatorPage.ErrorSubmittingChecklist'), error);
      alert(t('OperatorPage.ChecklistError'));
    }
  };

  // ===================================
  // NEW FUNCTION: handleEmailPdf
  // ===================================
const handleEmailPdf = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    alert(t('OperatorPage.NoTokenFound'));
    return;
  }

  // Parse comma-separated head users
const userIds = (headUser || '')
  .split(',')
  .map(id => id.trim())
  .filter(id => id !== '');


  if (userIds.length === 0) {
    alert(t('OperatorPage.NoResponsibleTechnicians'));
    return;
  }

  try {
    // Fetch each technician and collect eligible emails
    const emailPromises = userIds.map(async id => {
      const res = await axios.get(`${apiBaseUrl}/mnt/api/technicians/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const tech = res.data;
      return tech.receive_email === 1 && tech.useremail ? tech.useremail : null;
    });

    const emails = (await Promise.all(emailPromises)).filter(Boolean);

    if (emails.length === 0) {
      alert(t('OperatorPage.NoEligibleEmailRecipients'));
      return;
    }

    // Generate PDF
    const element = pdfContentRef.current;
    if (!element) {
      console.error('PDF content ref not found');
      return;
    }

    const canvas = await html2canvas(element, { scale: 1.5, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      unit: 'px',
      format: [canvas.width, canvas.height],
      orientation: 'portrait',
    });
    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    const pdfBlob = pdf.output('blob');

    const formData = new FormData();
    formData.append('pdfFile', pdfBlob, `schedule_${scheduleid}.pdf`);
    formData.append('recipientEmail', emails.join(','));

    formData.append('scheduleId', scheduleid);

    await axios.post(`${apiBaseUrl}/mnt/api/maintenance/sendSchedulePdf`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    });

    alert(t('OperatorPage.EmailSentSuccess'));
  } catch (err) {
    console.error('Error generating or sending PDF:', err);
    alert(t('OperatorPage.EmailSentError'));
  }
};


  if (!schedule) {
    return <div>{t('OperatorPage.Loading')}</div>;
  }

  return (
    <>
      <div className="content">
        <div className="top-menu">
          <div className="top-menu__divider"></div>
          <button
            type="button"
            className="button--blue"
            onClick={handleSubmit}
          >
            {isAllChecked()
              ? t('OperatorPage.SubmitChecklist')
              : t('OperatorPage.Save')}
          </button>
        </div>

        {/* ===================================== */}
        {/* Wrap everything we want in the PDF here */}
        {/* ===================================== */}
        <div className="sr-container" ref={pdfContentRef}>
          <div className="sr-section sr-header">
            {`${t('OperatorPage.MaintenanceFor')} ${machineName} (${moment
              .unix(schedule.next_maintenance_date)
              .format('YYYY-MM-DD')})`}
          </div>

          <div className="sr-section sr-desc">
            <div className="sr-desc__section-title">
              {t('OperatorPage.Description')}
            </div>
            <div className="sr-desc__section-desc">{schedule.note}</div>
          </div>

          <div className="sr-section sr-checklist">
            <div className="sr-checklist__section-title">
              {t('OperatorPage.Checklist')}
            </div>

            {schedule.checklist.map((item, index) => {
              const isHeader = item.status === 'header';
              const isText = item.status === 'text';
              const isFile = item.status === 'file';
              const fileUrl = `${apiBaseUrl}/mnt/api/uploads/${item.description}`;

              return (
                <React.Fragment key={item.checklistid}>
                  {isHeader ? (
                    <div className="sr-checklist__item-header">
                      {item.description}
                    </div>
                  ) : isText ? (
                    <textarea
                      className="sr-checklist__item-text"
                      value={item.description || ''}
                      onChange={(e) =>
                        handleTextChange(index, e.target.value)
                      }
                    />
                  ) : isFile ? (
                    <div className="sr-checklist__item-file">
                      {/\.(jpg|jpeg|png|gif)$/i.test(item.description) ? (
                        <div className="file-preview-image">
                          <img
                            src={fileUrl}
                            alt="Checklist Upload"
                            className="file-preview-image__img"
                            onClick={() => setZoomImage(fileUrl)}
                          />
                          <div className="file-preview-image__actions">
                            <a
                              href={fileUrl}
                              download
                              className="file-btn download"
                            >
                              {t('OperatorPage.Download')}
                            </a>
                            <button
                              className="file-btn zoom"
                              onClick={() => setZoomImage(fileUrl)}
                            >
                              {t('OperatorPage.Zoom')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="file-preview-doc">
                          <div className="file-preview-doc__name">
                            📄 {item.description.split(/[\\/]/).pop()}
                          </div>
                          <div className="file-preview-doc__actions">
                            <a
                              href={fileUrl}
                              download
                              className="file-btn download"
                            >
                              {t('OperatorPage.Download')}
                            </a>
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="file-btn open"
                            >
                              {t('OperatorPage.Open')}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      className={`sr-checklist__item-checkbox ${
                        item.status === 'yes'
                          ? 'sr-checklist__item-checked'
                          : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={item.status === 'yes'}
                        onChange={() => handleChecklistChange(index)}
                      />
                      <div className="sr-checklist__item-checkbox-desc">
                        {item.description}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <footer className="sr-footer">
            <button
              type="button"
              className="button--blue"
              onClick={handleSubmit}
            >
              {isAllChecked()
                ? t('OperatorPage.SubmitChecklist')
                : t('OperatorPage.Save')}
            </button>
                  {/* ================================ */}
      {/* NEW SECTION: Email PDF Controls */}
      {/* ================================ */}
      <div className="email-pdf-container" style={{ marginTop: '1.5rem' }}>
  <button
    type="button"
    className="button--green"
    onClick={handleEmailPdf}
  >
    {t('OperatorPage.EmailPdfButton')}
  </button>
</div>
          </footer>
        </div>
        {/* End of PDF-wrapper */}
      </div>

      {/* Zoomed image modal */}
      {zoomImage && (
        <div className="zoom-modal" onClick={() => setZoomImage(null)}>
          <div
            className="zoom-modal__content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={zoomImage}
              alt="Zoomed"
              className="zoom-modal__image"
            />
            <a href={zoomImage} download className="zoom-modal__download-button">
              Download Image
            </a>
          </div>
        </div>
      )}



      {/* ================================ */}
    </>
  );
};

export default OperatorPage;
